import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { api } from "../lib/api";
import { ArrowLeft, GitCompare, Loader2 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const SOURCE_COLORS = { internal: "#002EA6", cloudresearch: "#FF3B30", intercept: "#FFB800" };
const SOURCE_LABEL = { internal: "Internal", cloudresearch: "Programmatic", intercept: "Intercept" };

export default function CompareStudies() {
  const [search] = useSearchParams();
  const a = search.get("a");
  const b = search.get("b");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [pickA, setPickA] = useState(a || "");
  const [pickB, setPickB] = useState(b || "");

  useEffect(() => {
    (async () => {
      try {
        const { data: list } = await api.get("/campaigns");
        setCampaigns(list || []);
      } catch { /* dropdown stays empty; user can paste IDs directly */ }
    })();
  }, []);

  useEffect(() => {
    if (!a || !b) { setLoading(false); return; }
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/compare/campaigns`, { params: { a, b } });
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [a, b]);

  const launch = () => {
    if (!pickA || !pickB || pickA === pickB) return;
    window.location.href = `/client/compare?a=${pickA}&b=${pickB}`;
  };

  if (!a || !b) {
    return (
      <div className="min-h-screen bg-[var(--rl-bg-subtle)]">
        <Navigation />
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 mb-3">
            <GitCompare className="w-4 h-4 text-[var(--rl-brand)]" />
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Comparative studies</div>
          </div>
          <h1 className="font-display font-black text-4xl tracking-tight">Compare two campaigns</h1>
          <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Pick two campaigns to see their executive reports side-by-side — channels, themes, and quote highlights.</p>
          <div className="mt-8 bg-white border border-[var(--rl-border)] p-6 space-y-4" data-testid="compare-picker">
            <CampaignPicker label="Campaign A" value={pickA} onChange={setPickA} campaigns={campaigns} testid="picker-a" />
            <CampaignPicker label="Campaign B" value={pickB} onChange={setPickB} campaigns={campaigns} exclude={pickA} testid="picker-b" />
            <button
              onClick={launch}
              disabled={!pickA || !pickB || pickA === pickB}
              className="w-full bg-[var(--rl-text)] text-white py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-40"
              data-testid="compare-launch-btn"
            >
              Compare reports →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[var(--rl-brand)]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]">
      <Navigation />
      <div className="max-w-2xl mx-auto px-6 py-12 text-center">
        <h1 className="font-display font-black text-3xl">Unable to load comparison</h1>
        <p className="text-[var(--rl-accent)] mt-4">{error}</p>
        <Link to="/client/compare" className="inline-flex items-center gap-2 mt-6 text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]"><ArrowLeft className="w-3 h-3" /> Back</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="compare-report-page">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <GitCompare className="w-4 h-4 text-[var(--rl-brand)]" />
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Comparative report</div>
        </div>
        <h1 className="font-display font-black text-3xl">{data?.a?.campaign?.title} <span className="text-[var(--rl-text-secondary)]">vs</span> {data?.b?.campaign?.title}</h1>

        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <CampaignColumn label="A" payload={data?.a} />
          <CampaignColumn label="B" payload={data?.b} />
        </div>
      </div>
    </div>
  );
}

function CampaignPicker({ label, value, onChange, campaigns, exclude, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] mb-1.5">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
        data-testid={testid}
      >
        <option value="">— Select a campaign —</option>
        {campaigns.filter((c) => c.id !== exclude).map((c) => (
          <option key={c.id} value={c.id}>{c.title} ({c.current_completes}/{c.target_completes})</option>
        ))}
      </select>
    </div>
  );
}

function CampaignColumn({ label, payload }) {
  if (!payload) return null;
  const { campaign, by_source, qual } = payload;
  const pieData = Object.entries(by_source || {}).map(([k, v]) => ({ name: SOURCE_LABEL[k] || k, key: k, value: v }));
  return (
    <div className="bg-white border border-[var(--rl-border)] p-6" data-testid={`compare-col-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-accent)]">Campaign {label}</div>
      <h2 className="font-display font-black text-2xl mt-1">{campaign.title}</h2>
      <div className="text-sm text-[var(--rl-text-secondary)] mt-1 font-mono">{campaign.current_completes} / {campaign.target_completes} completes · {campaign.status}</div>

      {/* Channel mix */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Channel mix</div>
        <div className="h-32 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={28} outerRadius={56}>
                {pieData.map((d) => <Cell key={d.key} fill={SOURCE_COLORS[d.key] || "#5E5F6E"} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* TL;DR */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Executive TL;DR</div>
        <ul className="mt-3 space-y-2">
          {(qual?.tldr || []).length === 0 ? (
            <li className="text-sm text-[var(--rl-text-secondary)]">More responses needed.</li>
          ) : (
            qual.tldr.map((b, i) => (
              <li key={`${label}-tldr-${i}`} className="text-sm leading-relaxed flex gap-3">
                <span className="font-mono text-[var(--rl-accent)] font-bold">{i + 1}.</span>
                <span>{b}</span>
              </li>
            ))
          )}
        </ul>
      </div>

      {/* Top themes */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Themes</div>
        <div className="mt-3 space-y-2">
          {(qual?.themes || []).slice(0, 5).map((t, i) => (
            <div key={`${label}-th-${i}-${t.name}`} className="flex items-baseline justify-between border-b border-[var(--rl-border)] pb-2">
              <div className="text-sm font-semibold">{t.name}</div>
              <div className="font-mono text-xs">{t.percentage}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
